var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property } from 'lit/decorators.js';
/**
 * A snackbar component for displaying brief notification messages.
 *
 * Snackbars appear at the bottom-left of the screen and automatically
 * dismiss after a configurable duration. Multiple snackbars stack vertically.
 *
 * @fires dismiss - Fired when the snackbar is dismissed (manually or automatically)
 *
 * @example
 * ```ts
 * // Show a success message
 * KRSnackbar.show({ message: 'Item saved successfully', type: 'success' });
 *
 * // Show a message with title
 * KRSnackbar.show({ title: 'Upload Complete', message: 'Your file has been uploaded.', type: 'success' });
 *
 * // Show an error that stays until manually dismissed
 * KRSnackbar.show({ message: 'Failed to save', type: 'error', duration: 0 });
 *
 * // Show a message with a link
 * KRSnackbar.show({ message: 'Import queued.', action: { label: 'View Import', href: '/imports' } });
 * ```
 */
export class KRSnackbar extends LitElement {
    constructor() {
        super(...arguments);
        this.dismissTimeout = null;
        /**
         * The snackbar type/severity. Controls the color scheme and icon.
         */
        this.type = 'info';
        /**
         * Optional title to display above the message.
         */
        this.title = '';
        /**
         * The message to display in the snackbar.
         */
        this.message = '';
        /**
         * Duration in milliseconds before auto-dismiss. Set to 0 to disable auto-dismiss.
         * @default 5000
         */
        this.duration = 5000;
        /**
         * Optional link shown under the message. Clicking it dismisses the snackbar.
         */
        this.action = null;
    }
    /**
     * Show a snackbar with the given options.
     * @param options - Configuration including message, optional title, type, and duration
     * @returns The created snackbar element
     */
    static show(options) {
        // Close any existing snackbars before showing the new one
        for (const existing of this.activeSnackbars) {
            existing.dismiss();
        }
        const snackbar = document.createElement('kr-snackbar');
        snackbar.message = options.message;
        snackbar.title = options.title ?? '';
        snackbar.type = options.type ?? 'info';
        if (options.action) {
            snackbar.action = options.action;
        }
        if (options.duration !== undefined) {
            snackbar.duration = options.duration;
        }
        else if (snackbar.type === 'error') {
            snackbar.duration = 0;
        }
        else {
            snackbar.duration = 5000;
        }
        document.body.appendChild(snackbar);
        return snackbar;
    }
    connectedCallback() {
        super.connectedCallback();
        // Set role and type class on host
        this.setAttribute('role', 'alert');
        this.classList.add(`kr-snackbar--${this.type}`);
        if (this.title) {
            this.classList.add('kr-snackbar--has-title');
        }
        // Add to active snackbars and update positions
        KRSnackbar.activeSnackbars.push(this);
        // this.updatePositions();
        // Set up auto-dismiss
        if (this.duration > 0) {
            this.dismissTimeout = window.setTimeout(() => {
                this.dismiss();
            }, this.duration);
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        // Remove from active snackbars
        const index = KRSnackbar.activeSnackbars.indexOf(this);
        if (index > -1) {
            KRSnackbar.activeSnackbars.splice(index, 1);
        }
        // Update positions of remaining snackbars
        // this.updatePositions();
        // Clear timeout
        if (this.dismissTimeout) {
            clearTimeout(this.dismissTimeout);
        }
    }
    // private updatePositions() {
    //   let bottomOffset = 24;
    //   for (const snackbar of KRSnackbar.activeSnackbars) {
    //     snackbar.style.bottom = `${bottomOffset}px`;
    //     bottomOffset += snackbar.offsetHeight + 8;
    //   }
    // }
    dismiss() {
        if (this.dismissTimeout) {
            clearTimeout(this.dismissTimeout);
            this.dismissTimeout = null;
        }
        this.classList.add('kr-snackbar--closing');
        this.addEventListener('animationend', () => {
            this.dispatchEvent(new CustomEvent('dismiss', {
                bubbles: true,
                composed: true
            }));
            this.remove();
        }, { once: true });
    }
    render() {
        const icons = {
            info: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clip-rule="evenodd"
          />
        </svg>
      `,
            success: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
      `,
            warning: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
      `,
            error: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
      `,
        };
        return html `
      ${icons[this.type]}
      <div class="content">
        ${this.title ? html `<div class="title">${this.title}</div>` : ''}
        <div class="message">${this.message}</div>
        ${this.action
            ? html `<a
              class="action"
              href=${this.action.href}
              @click=${this.dismiss}
            >${this.action.label}</a>`
            : ''}
      </div>
      <button
        class="dismiss"
        type="button"
        aria-label="Dismiss"
        @click=${this.dismiss}
      >
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          width="16"
          height="16"
        >
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    `;
    }
}
KRSnackbar.styles = css `
    :host,
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    :host {
      position: fixed;
      left: 16px;
      bottom: 24px;
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 12px 14px 16px;
      border-radius: 8px;
      border: 1px solid;
      border-left: 4px solid;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      min-width: 280px;
      max-width: 400px;
      min-height: 48px;
      animation: slideIn 0.2s ease-out;
    }

    :host(.kr-snackbar--has-title) {
      align-items: flex-start;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateX(-100%);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes slideOut {
      from {
        opacity: 1;
        transform: translateX(0);
      }
      to {
        opacity: 0;
        transform: translateX(-100%);
      }
    }

    :host(.kr-snackbar--closing) {
      animation: slideOut 0.15s ease-in forwards;
    }

    /* Variants */
    :host(.kr-snackbar--info) {
      background-color: #eff6ff;
      border-color: #bfdbfe;
      border-left-color: #3b82f6;
    }

    :host(.kr-snackbar--info) .icon,
    :host(.kr-snackbar--info) .action {
      color: #2563eb;
    }

    :host(.kr-snackbar--success) {
      background-color: #f0fdf4;
      border-color: #bbf7d0;
      border-left-color: #22c55e;
    }

    :host(.kr-snackbar--success) .icon,
    :host(.kr-snackbar--success) .action {
      color: #16a34a;
    }

    :host(.kr-snackbar--warning) {
      background-color: #fffbeb;
      border-color: #fde68a;
      border-left-color: #f59e0b;
    }

    :host(.kr-snackbar--warning) .icon,
    :host(.kr-snackbar--warning) .action {
      color: #d97706;
    }

    :host(.kr-snackbar--error) {
      background-color: #fef2f2;
      border-color: #fecaca;
      border-left-color: #ef4444;
    }

    :host(.kr-snackbar--error) .icon,
    :host(.kr-snackbar--error) .action {
      color: #dc2626;
    }

    .icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      margin-top: 1px;
    }

    .content {
      flex: 1;
      min-width: 0;
    }

    .title {
      font-size: 14px;
      font-weight: 600;
      color: #000000;
      margin: 0 0 2px 0;
    }

    .message {
      font-size: 14px;
      color: #000000;
      margin: 0;
    }

    /* The link takes the variant's accent color, set alongside the icon above. */
    .action {
      display: inline-block;
      margin-top: 6px;
      font-size: 14px;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
      cursor: pointer;
    }

    .action:hover {
      text-decoration-thickness: 2px;
    }

    .action:focus-visible {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
      border-radius: 2px;
    }

    .dismiss {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: none;
      border: none;
      border-radius: 4px;
      color: #6b7280;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }

    .dismiss:hover {
      background-color: rgba(0, 0, 0, 0.05);
      color: #374151;
    }

    .dismiss:focus-visible {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
    }
  `;
/** Track active snackbars for stacking */
KRSnackbar.activeSnackbars = [];
__decorate([
    property({ type: String })
], KRSnackbar.prototype, "type", void 0);
__decorate([
    property({ type: String })
], KRSnackbar.prototype, "title", void 0);
__decorate([
    property({ type: String })
], KRSnackbar.prototype, "message", void 0);
__decorate([
    property({ type: Number })
], KRSnackbar.prototype, "duration", void 0);
__decorate([
    property({ attribute: false })
], KRSnackbar.prototype, "action", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-snackbar')) {
    customElements.define('kr-snackbar', KRSnackbar);
}
//# sourceMappingURL=snackbar.js.map